<template>
  <v-container class="my-questions">
    <div class="d-flex flex-wrap align-center my-questions__head">
      <h1 class="my-questions__title">{{ t("title") }}</h1>
      <v-btn depressed color="primary" :href="forumUrl + '?ask=1'">{{ t("ask") }}</v-btn>
    </div>

    <div v-if="loading" class="text-center py-10">
      <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
    </div>

    <div v-else-if="!items.length" class="my-questions__empty">
      <p>{{ t("empty") }}</p>
      <v-btn depressed outlined color="primary" :href="forumUrl">{{ t("toForum") }}</v-btn>
    </div>

    <div
      v-for="item in items"
      v-else
      :id="'question-' + item.id"
      :key="item.id"
      class="my-questions__card"
      :class="{ 'my-questions__card--new': item.new_answers, 'my-questions__card--open': open[item.id] }"
    >
      <!-- the question folds into its title; opened, it shows the answers and the dialog under each of them -->
      <div class="my-questions__toggle" role="button" tabindex="0" @click="toggle(item)" @keydown.enter="toggle(item)">
        <div class="my-questions__toggle-main">
          <div class="d-flex flex-wrap align-center my-questions__meta">
            <span v-if="item.new_answers" class="my-questions__new">{{ t("new") }} {{ item.new_answers }}</span>
            <span v-if="item.status === 'pending'" class="my-questions__pending">{{ t("pending") }}</span>
            <span v-if="item.category">{{ item.category.name }}</span>
            <span>{{ dateOf(item.created_at) }}</span>
          </div>
          <div class="my-questions__question">{{ item.title }}</div>
          <div class="my-questions__answers">{{ answersText(item.answers_count) }}</div>
        </div>
        <v-icon class="my-questions__chevron">{{ open[item.id] ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
      </div>

      <template v-if="open[item.id]">
        <p class="my-questions__body">{{ item.body }}</p>
        <a v-if="item.status === 'published'" :href="questionUrl(item)" target="_blank" rel="noopener" class="my-questions__site">{{ t("openOnSite") }}</a>

        <div v-for="answer in item.answers" :key="answer.id" class="my-questions__answer">
          <div class="d-flex align-center my-questions__author">
            <img :src="answer.psychologist.photo" alt="" class="my-questions__photo">
            <span class="my-questions__name">{{ answer.psychologist.full_name }}</span>
            <span v-if="answer.is_new" class="my-questions__mark">{{ t("newMark") }}</span>
          </div>
          <p class="my-questions__text">{{ answer.body }}</p>

          <div v-if="answer.replies.length" class="my-questions__thread">
            <div v-for="reply in answer.replies" :key="reply.id" class="my-questions__reply">
              <div class="d-flex flex-wrap align-center my-questions__reply-head">
                <span class="my-questions__name">{{ reply.from === "author" ? t("you") : answer.psychologist.full_name }}</span>
                <span>{{ timeOf(reply.created_at) }}</span>
                <span v-if="reply.is_new" class="my-questions__mark">{{ t("newMark") }}</span>
              </div>
              <p class="my-questions__text">{{ reply.body }}</p>
            </div>
          </div>

          <div class="my-questions__form">
            <!-- a fixed height with its own scroll instead of auto-grow (see the site's AskForm) -->
            <v-textarea
              v-model="drafts[answer.id]"
              class="my-questions__input"
              :label="t('clarify')"
              :hint="t('clarifyHint')"
              counter="5000"
              maxlength="5000"
              rows="3"
              outlined
              dense
            ></v-textarea>
            <v-btn depressed color="primary" :loading="sending === answer.id" @click="send(item, answer)">{{ t("send") }}</v-btn>
          </div>
        </div>
      </template>
    </div>
  </v-container>
</template>

<script>
import Vue from "vue";
import ApiClient from "@/libs/http-client";

export default {
  name: "MyQuestions",
  metaInfo() {
    return { title: "Платформа / " + this.t("title") };
  },
  data() {
    return {
      loading: true,
      items: [],
      open: {},
      drafts: {},
      sending: null,
      forumUrl: process.env.VUE_APP_MAIN_PAGE + "/forum",
    };
  },
  mounted() {
    ApiClient.get("api/patients/forum/my-questions")
      .then((response) => {
        this.items = response.data.data;
        this.openFromLink();
      })
      .catch(() => {})
      .finally(() => {
        this.loading = false;
      });
  },
  methods: {
    t(key) {
      return this.$vuetify.lang.t(`$vuetify.main.patientDashboard.myQuestions.${key}`);
    },
    questionUrl(item) {
      return this.forumUrl + "/q/" + item.path;
    },
    // "Уточніть у психолога в кабінеті" on the question's page: ?q=<id> opens that question
    openFromLink() {
      const id = parseInt(this.$route.query.q, 10);
      const item = this.items.find((other) => other.id === id);
      if (!item) return;
      this.toggle(item);
      // after the cabinet's header and footer have settled; below the fixed header
      setTimeout(() => {
        const card = document.getElementById("question-" + id);
        if (card) window.scrollTo({ top: card.getBoundingClientRect().top + window.pageYOffset - 80 });
      }, 400);
    },
    // opened, its answers and the psychologists' messages stop being "new" (the marks stay until reload)
    toggle(item) {
      Vue.set(this.open, item.id, !this.open[item.id]);
      if (!this.open[item.id] || !item.new_answers) return;
      ApiClient.post(`api/patients/forum/my-questions/${item.id}/seen`)
        .then(() => {
          item.new_answers = 0;
          this.$root.$emit("menu-counters-refresh");
        })
        .catch(() => {});
    },
    send(item, answer) {
      const body = (this.drafts[answer.id] || "").trim();
      if (body.length < 2) {
        this.toast(this.t("tooShort"), "error");
        return;
      }
      this.sending = answer.id;
      ApiClient.post(`api/patients/forum/answers/${answer.id}/replies`, { body })
        .then((response) => {
          if (!response) return;
          answer.replies.push(response.data.data);
          Vue.set(this.drafts, answer.id, "");
          this.toast(response.data.message);
        })
        .catch(() => {
          this.toast(this.t("failed"), "error");
        })
        .finally(() => {
          this.sending = null;
        });
    },
    answersText(count) {
      if (!count) return this.t("noAnswers");
      const forms = this.t("answerForms").split("|");
      const mod10 = count % 10;
      const mod100 = count % 100;
      const form = mod10 === 1 && mod100 !== 11 ? forms[0]
        : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? forms[1]
          : forms[2];
      return `${count} ${form}`;
    },
    dateOf(value) {
      return new Date(value).toLocaleDateString("uk-UA", { day: "numeric", month: "long", year: "numeric" });
    },
    timeOf(value) {
      return new Date(value).toLocaleString("uk-UA", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
    },
    toast(text, icon = "success") {
      Vue.swal({
        target: document.getElementById("main"),
        text,
        icon,
        timer: 3000,
        toast: true,
        position: "top-right",
        showConfirmButton: false,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.my-questions {
  max-width: 900px;
  padding-top: 40px;
  padding-bottom: 60px;
}
.my-questions__head {
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}
.my-questions__title {
  color: #00A3B7;
  font-size: 25px;
  font-weight: 500;
}
.my-questions__empty {
  padding: 40px 0;
  color: #575756;
  font-size: 17px;
  text-align: center;
}
.my-questions__card {
  margin-bottom: 14px;
  padding: 18px 22px;
  border: 1px solid #D9E4EB;
  border-radius: 12px;
  background: #fff;
  color: #575756;
  transition: box-shadow 0.2s, border-color 0.2s;
  &:hover {
    border-color: #00A3B7;
    box-shadow: 0 6px 24px rgba(0, 163, 183, 0.12);
  }
  &--new,
  &--open {
    border-color: #00A3B7;
  }
}
.my-questions__toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  outline: none;
}
.my-questions__toggle-main {
  flex: 1 1 auto;
  min-width: 0;
}
.my-questions__chevron {
  flex: none;
}
.my-questions__meta {
  gap: 6px 14px;
  color: #868686;
  font-size: 14px;
}
.my-questions__new {
  color: #E5484D;
  font-weight: 500;
}
.my-questions__pending {
  color: #C77700;
}
.my-questions__question {
  margin-top: 8px;
  color: #00A3B7;
  font-size: 18px;
  font-weight: 500;
  overflow-wrap: break-word;
}
.my-questions__answers {
  margin-top: 6px;
  font-size: 15px;
}
.my-questions__body {
  margin: 14px 0 6px;
  line-height: 1.45;
  white-space: pre-line;
  overflow-wrap: break-word;
}
.my-questions__site {
  display: inline-block;
  margin-bottom: 4px;
  font-size: 14px;
  text-decoration: none;
}
.my-questions__answer {
  margin-top: 14px;
  padding: 14px 16px;
  background: #F4F8FA;
  border-radius: 10px;
}
.my-questions__author {
  gap: 10px;
}
.my-questions__photo {
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  object-position: center 25%;
}
.my-questions__name {
  color: #575756;
  font-weight: 500;
}
.my-questions__mark {
  color: #E5484D;
  font-size: 13px;
  font-weight: 500;
}
.my-questions__text {
  margin: 8px 0 0;
  line-height: 1.45;
  white-space: pre-line;
  overflow-wrap: break-word;
}
// the dialog is a level lower than the answer
.my-questions__thread {
  margin: 12px 0 0 50px;
  padding-left: 14px;
  border-left: 2px solid #D9E4EB;
}
.my-questions__reply + .my-questions__reply {
  margin-top: 12px;
}
.my-questions__reply-head {
  gap: 4px 10px;
  color: #868686;
  font-size: 13px;
}
.my-questions__form {
  margin: 14px 0 0 50px;
  ::v-deep textarea {
    overflow-y: auto;
    resize: vertical;
    max-height: 50vh;
  }
}
@media (max-width: 600px) {
  .my-questions__card {
    padding: 16px 14px;
  }
  .my-questions__answer {
    padding: 12px;
  }
  .my-questions__thread,
  .my-questions__form {
    margin-left: 0;
  }
  .my-questions__thread {
    padding-left: 10px;
  }
}
</style>
